import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

interface BusinessAccountingCategory {
  id: number;
  name: string;
}

interface CategoryWithMapping {
  id: number;
  name: string;
  color: string;
  business_accounting_category_id?: number | null;
  business_accounting_category_name?: string | null;
  business_transaction_count: number;
  subcategories: SubcategoryWithMapping[];
}

interface SubcategoryWithMapping {
  id: number;
  name: string;
  business_accounting_category_id?: number | null;
  business_accounting_category_name?: string | null;
  business_transaction_count: number;
}

interface UnmappedCategory {
  category_id: number;
  category_name: string;
  subcategory_id?: number | null;
  subcategory_name?: string | null;
  business_transaction_count: number;
}

const BusinessAccountingMapping: React.FC = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<CategoryWithMapping[]>([]);
  const [businessCategories, setBusinessCategories] = useState<BusinessAccountingCategory[]>([]);
  const [unmappedCategories, setUnmappedCategories] = useState<UnmappedCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'mappings' | 'validation' | 'settings'>('settings');
  const [categorySettings, setCategorySettings] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [categoriesData, businessCategoriesData, unmappedData, settingsData] = await Promise.all([
        api.businessAccounting.getCategoriesWithMappings(),
        api.businessAccounting.getBusinessAccountingCategories(),
        api.businessAccounting.validateMappings(),
        api.businessAccounting.getCategorySettings(selectedYear)
      ]);
      
      setCategories(categoriesData);
      setBusinessCategories(businessCategoriesData);
      setUnmappedCategories(unmappedData);
      setCategorySettings(settingsData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async (categoryId: number, subcategoryId: number | null | undefined, businessCategoryId: number | null) => {
    try {
      if (businessCategoryId) {
        await api.businessAccounting.upsertCategoryMapping({
          category_id: categoryId,
          subcategory_id: subcategoryId || null,
          business_accounting_category_id: businessCategoryId
        });
      } else {
        // If null/empty, delete the mapping
        await api.businessAccounting.deleteCategoryMapping({
          category_id: categoryId,
          subcategory_id: subcategoryId || null
        });
      }
      
      // Update local state immediately without full reload to prevent screen jump
      setCategories(prev => prev.map(cat => {
        if (cat.id === categoryId) {
          const updatedCat = { ...cat };
          if (subcategoryId) {
            updatedCat.subcategories = cat.subcategories.map(sub => 
              sub.id === subcategoryId 
                ? { ...sub, business_accounting_category_id: businessCategoryId, business_accounting_category_name: businessCategories.find(bc => bc.id === businessCategoryId)?.name || null }
                : sub
            );
          } else {
            updatedCat.business_accounting_category_id = businessCategoryId;
            updatedCat.business_accounting_category_name = businessCategories.find(bc => bc.id === businessCategoryId)?.name || null;
          }
          return updatedCat;
        }
        return cat;
      }));
    } catch (err: any) {
      console.error('Error saving mapping:', err);
      alert(err.message || 'Failed to save mapping');
      // Reload on error to get correct state
      await loadData();
    }
  };

  const handleDeleteMapping = async (categoryId: number, subcategoryId?: number | null) => {
    if (!window.confirm('Are you sure you want to remove this mapping?')) return;
    
    try {
      await api.businessAccounting.deleteCategoryMapping({
        category_id: categoryId,
        subcategory_id: subcategoryId || null
      });
      
      await loadData();
    } catch (err: any) {
      console.error('Error deleting mapping:', err);
      alert(err.message || 'Failed to delete mapping');
    }
  };


  const renderCategoryRow = (category: CategoryWithMapping) => {
    const hasBusinessExpenses = category.business_transaction_count > 0;
    const hasMapping = !!category.business_accounting_category_id;
    const needsMapping = hasBusinessExpenses && !hasMapping;
    
    return (
      <React.Fragment key={category.id}>
        <tr 
          className={needsMapping ? 'needs-mapping' : ''}
          style={{ 
            borderColor: 'var(--border-color)',
            backgroundColor: needsMapping ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
          }}
        >
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center">
              <div 
                className="w-4 h-4 rounded-full mr-3"
                style={{ backgroundColor: category.color }}
              ></div>
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {category.name}
              </span>
              {needsMapping && (
                <span className="ml-2 text-xs px-2 py-1 rounded" style={{ 
                  backgroundColor: 'rgba(239, 68, 68, 0.2)', 
                  color: '#ef4444',
                  fontWeight: '500'
                }}>
                  ⚠️ Mapping Required
                </span>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
            <select
              value={category.business_accounting_category_id || ''}
              onChange={(e) => {
                const businessCategoryId = e.target.value ? parseInt(e.target.value) : null;
                handleSaveMapping(category.id, null, businessCategoryId);
              }}
              className="px-2 py-1 text-xs rounded border w-full"
              style={{ 
                backgroundColor: 'var(--background-light)',
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
            >
              <option value=""></option>
              {businessCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
            {category.business_transaction_count}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            {hasMapping && (
              <button
                onClick={() => handleDeleteMapping(category.id)}
                className="px-3 py-1 text-xs rounded"
                style={{ 
                  backgroundColor: 'var(--danger-color)',
                  color: 'white'
                }}
              >
                🗑️ Remove
              </button>
            )}
          </td>
        </tr>
        
        {/* Subcategories */}
        {category.subcategories.map((subcategory) => {
          const subHasBusinessExpenses = subcategory.business_transaction_count > 0;
          const subHasMapping = !!subcategory.business_accounting_category_id;
          const subNeedsMapping = subHasBusinessExpenses && !subHasMapping;
          
          return (
            <tr
              key={`${category.id}-${subcategory.id}`}
              className={subNeedsMapping ? 'needs-mapping' : ''}
              style={{ 
                borderColor: 'var(--border-color)',
                backgroundColor: subNeedsMapping ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
              }}
            >
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="flex items-center pl-12">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: category.color, opacity: 0.7 }}
                  ></div>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {subcategory.name}
                  </span>
                  {subNeedsMapping && (
                    <span className="ml-2 text-xs px-2 py-1 rounded" style={{ 
                      backgroundColor: 'rgba(239, 68, 68, 0.2)', 
                      color: '#ef4444',
                      fontWeight: '500'
                    }}>
                      ⚠️ Mapping Required
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                <select
                  value={subcategory.business_accounting_category_id || ''}
                  onChange={(e) => {
                    const businessCategoryId = e.target.value ? parseInt(e.target.value) : null;
                    handleSaveMapping(category.id, subcategory.id, businessCategoryId);
                  }}
                  className="px-2 py-1 text-xs rounded border w-full"
                  style={{ 
                    backgroundColor: 'var(--background-light)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value=""></option>
                  {businessCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                {subcategory.business_transaction_count}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm">
                {subHasMapping && (
                  <button
                    onClick={() => handleDeleteMapping(category.id, subcategory.id)}
                    className="px-3 py-1 text-xs rounded"
                    style={{ 
                      backgroundColor: 'var(--danger-color)',
                      color: 'white'
                    }}
                  >
                    🗑️ Remove
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4">❌</div>
          <p style={{ color: 'var(--danger-color)' }}>{error}</p>
          <button 
            onClick={loadData}
            className="mt-4 px-4 py-2 rounded"
            style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Business Accounting Category Mappings
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Map your Wealthini categories to business accounting categories for expense reporting
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'settings' 
                ? 'border-b-2' 
                : ''
            }`}
            style={{
              color: activeTab === 'settings' ? 'var(--primary-color)' : 'var(--text-secondary)',
              borderBottomColor: activeTab === 'settings' ? 'var(--primary-color)' : 'transparent'
            }}
          >
            Billable to Business
          </button>
          <button
            onClick={() => setActiveTab('mappings')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'mappings' 
                ? 'border-b-2' 
                : ''
            }`}
            style={{
              color: activeTab === 'mappings' ? 'var(--primary-color)' : 'var(--text-secondary)',
              borderBottomColor: activeTab === 'mappings' ? 'var(--primary-color)' : 'transparent'
            }}
          >
            Category Mappings
            {unmappedCategories.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ 
                backgroundColor: 'var(--danger-color)', 
                color: 'white' 
              }}>
                {unmappedCategories.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('validation')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'validation' 
                ? 'border-b-2' 
                : ''
            }`}
            style={{
              color: activeTab === 'validation' ? 'var(--primary-color)' : 'var(--text-secondary)',
              borderBottomColor: activeTab === 'validation' ? 'var(--primary-color)' : 'transparent'
            }}
          >
            Validation
            {unmappedCategories.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ 
                backgroundColor: 'var(--danger-color)', 
                color: 'white' 
              }}>
                {unmappedCategories.length} Missing
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="rounded-lg shadow-sm border" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                  Personal Expenses Billable to Business
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Set the business percentage and tax type for each business accounting category. Totals are calculated from {selectedYear} transactions.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Year:
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-3 py-2 rounded border text-sm"
                    style={{
                      backgroundColor: 'var(--background-light)',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <button
                onClick={() => {
                  // Prepare CSV data
                  const headers = ['Category', 'Business %', 'Tax', 'Total', 'Business Total', 'Pre-tax', 'HST'];
                  const rows = categorySettings.map(setting => {
                    const total = parseFloat(setting.total) || 0;
                    const businessPercentage = parseFloat(setting.business_percentage) || 0;
                    const businessTotal = total * businessPercentage;
                    const preTax = setting.tax_type === 'HST' ? businessTotal / 1.13 : businessTotal;
                    const hst = setting.tax_type === 'HST' ? businessTotal - preTax : 0;
                    
                    return [
                      setting.business_accounting_category_name,
                      businessPercentage.toFixed(2),
                      setting.tax_type || '',
                      total.toFixed(2),
                      businessTotal.toFixed(2),
                      preTax.toFixed(2),
                      hst.toFixed(2)
                    ];
                  });
                  
                  // Add totals row
                  const totalRow = ['TOTAL', '', '', 
                    categorySettings.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0).toFixed(2),
                    categorySettings.reduce((sum, s) => {
                      const total = parseFloat(s.total) || 0;
                      const businessPercentage = parseFloat(s.business_percentage) || 0;
                      return sum + (total * businessPercentage);
                    }, 0).toFixed(2),
                    categorySettings.reduce((sum, s) => {
                      const total = parseFloat(s.total) || 0;
                      const businessPercentage = parseFloat(s.business_percentage) || 0;
                      const businessTotal = total * businessPercentage;
                      const preTax = s.tax_type === 'HST' ? businessTotal / 1.13 : businessTotal;
                      return sum + preTax;
                    }, 0).toFixed(2),
                    categorySettings.reduce((sum, s) => {
                      const total = parseFloat(s.total) || 0;
                      const businessPercentage = parseFloat(s.business_percentage) || 0;
                      const businessTotal = total * businessPercentage;
                      const preTax = s.tax_type === 'HST' ? businessTotal / 1.13 : businessTotal;
                      const hst = s.tax_type === 'HST' ? businessTotal - preTax : 0;
                      return sum + hst;
                    }, 0).toFixed(2)
                  ];
                  
                  // Combine headers and rows
                  const csvContent = [
                    headers.join(','),
                    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
                    totalRow.map(cell => `"${cell}"`).join(',')
                  ].join('\n');
                  
                  // Create blob and download
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  link.setAttribute('download', `${selectedYear} - Personal Expenses billable to Business.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="px-4 py-2 rounded text-sm font-medium"
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Export
              </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                <thead style={{ backgroundColor: 'var(--background-light)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Business %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Tax
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Business Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Pre-tax
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      HST
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                  {categorySettings.map((setting) => {
                    const isMortgageInterest = setting.business_accounting_category_name === 'Mortgage - Interest';
                    const total = parseFloat(setting.total) || 0;
                    const businessPercentage = parseFloat(setting.business_percentage) || 0;
                    const businessTotal = total * businessPercentage;
                    const preTax = setting.tax_type === 'HST' ? businessTotal / 1.13 : businessTotal;
                    const hst = setting.tax_type === 'HST' ? businessTotal - preTax : 0;
                    
                    return (
                      <tr key={setting.business_accounting_category_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {setting.business_accounting_category_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={businessPercentage}
                            onChange={async (e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value >= 0 && value <= 1) {
                                try {
                                  await api.businessAccounting.updateCategorySettings({
                                    business_accounting_category_id: setting.business_accounting_category_id,
                                    business_percentage: value,
                                    tax_type: setting.tax_type || '',
                                    manual_total: isMortgageInterest ? total : undefined
                                  });
                                  // Reload to get updated calculations
                                  await loadData();
                                } catch (err: any) {
                                  console.error('Error updating setting:', err);
                                  alert(err.message || 'Failed to update setting');
                                }
                              }
                            }}
                            className="px-2 py-1 text-xs rounded border w-20"
                            style={{ 
                              backgroundColor: 'var(--background-light)',
                              borderColor: 'var(--border-color)',
                              color: 'var(--text-primary)'
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={setting.tax_type || ''}
                            onChange={async (e) => {
                              const taxType = e.target.value;
                              try {
                                await api.businessAccounting.updateCategorySettings({
                                  business_accounting_category_id: setting.business_accounting_category_id,
                                  business_percentage: businessPercentage,
                                  tax_type: taxType,
                                  manual_total: isMortgageInterest ? total : undefined
                                });
                                // Reload to get updated calculations
                                await loadData();
                              } catch (err: any) {
                                console.error('Error updating setting:', err);
                                alert(err.message || 'Failed to update setting');
                              }
                            }}
                            className="px-2 py-1 text-xs rounded border"
                            style={{ 
                              backgroundColor: 'var(--background-light)',
                              borderColor: 'var(--border-color)',
                              color: 'var(--text-primary)',
                              minWidth: '120px'
                            }}
                          >
                            <option value="">--</option>
                            <option value="HST">HST</option>
                            <option value="NO HST">NO HST</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right" style={{ color: 'var(--text-primary)' }}>
                          {isMortgageInterest ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={total}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                // Update local state immediately for responsive UI
                                setCategorySettings(prev => prev.map(s => 
                                  s.business_accounting_category_id === setting.business_accounting_category_id
                                    ? { ...s, total: value }
                                    : s
                                ));
                              }}
                              onBlur={async (e) => {
                                const value = parseFloat(e.target.value) || 0;
                                if (!isNaN(value) && value >= 0) {
                                  try {
                                    await api.businessAccounting.updateCategorySettings({
                                      business_accounting_category_id: setting.business_accounting_category_id,
                                      business_percentage: businessPercentage,
                                      tax_type: setting.tax_type || '',
                                      manual_total: value
                                    });
                                    // Only reload on blur to get server confirmation, but UI already updated
                                    await loadData();
                                  } catch (err: any) {
                                    console.error('Error updating setting:', err);
                                    alert(err.message || 'Failed to update setting');
                                    // Reload on error to get correct state
                                    await loadData();
                                  }
                                }
                              }}
                              className="px-2 py-1 text-xs rounded border w-24 text-right"
                              style={{ 
                                backgroundColor: 'var(--background-light)',
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                              }}
                            />
                          ) : (
                            formatCurrency(total)
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right" style={{ color: 'var(--text-primary)' }}>
                          {formatCurrency(businessTotal)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right" style={{ color: 'var(--text-primary)' }}>
                          {formatCurrency(preTax)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right" style={{ color: 'var(--text-primary)' }}>
                          {formatCurrency(hst)}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  <tr className="font-bold" style={{ borderTop: '2px solid var(--border-color)', backgroundColor: 'var(--background-light)' }}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                      TOTAL
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap"></td>
                    <td className="px-6 py-4 whitespace-nowrap"></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(categorySettings.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(categorySettings.reduce((sum, s) => {
                        const total = parseFloat(s.total) || 0;
                        const businessPercentage = parseFloat(s.business_percentage) || 0;
                        const businessTotal = total * businessPercentage;
                        return sum + businessTotal;
                      }, 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(categorySettings.reduce((sum, s) => {
                        const total = parseFloat(s.total) || 0;
                        const businessPercentage = parseFloat(s.business_percentage) || 0;
                        const businessTotal = total * businessPercentage;
                        const preTax = s.tax_type === 'HST' ? businessTotal / 1.13 : businessTotal;
                        return sum + preTax;
                      }, 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(categorySettings.reduce((sum, s) => {
                        const total = parseFloat(s.total) || 0;
                        const businessPercentage = parseFloat(s.business_percentage) || 0;
                        const businessTotal = total * businessPercentage;
                        const preTax = s.tax_type === 'HST' ? businessTotal / 1.13 : businessTotal;
                        const hst = s.tax_type === 'HST' ? businessTotal - preTax : 0;
                        return sum + hst;
                      }, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Mappings Tab */}
      {activeTab === 'mappings' && (
        <div className="rounded-lg shadow-sm border" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
              Category Mappings
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Map your categories and subcategories to business accounting categories. 
              Categories with business expenses require a mapping.
            </p>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                <thead style={{ backgroundColor: 'var(--background-light)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Category / Subcategory
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Business Accounting Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Business Transactions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                  {categories.map(renderCategoryRow)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Validation Tab */}
      {activeTab === 'validation' && (
        <div className="rounded-lg shadow-sm border" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
              Mapping Validation
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Categories and subcategories with business expenses that need mapping
            </p>
          </div>
          <div className="p-6">
            {unmappedCategories.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">✅</div>
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  All mappings complete!
                </h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  All categories with business expenses have been mapped.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  <thead style={{ backgroundColor: 'var(--background-light)' }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Category / Subcategory
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Business Transactions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                    {unmappedCategories.map((unmapped) => (
                      <tr key={`${unmapped.category_id}-${unmapped.subcategory_id || 'cat'}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              {unmapped.category_name}
                              {unmapped.subcategory_name && (
                                <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  - {unmapped.subcategory_name}
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {unmapped.business_transaction_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <select
                            value=""
                            onChange={(e) => {
                              const businessCategoryId = e.target.value ? parseInt(e.target.value) : null;
                              if (businessCategoryId) {
                                handleSaveMapping(unmapped.category_id, unmapped.subcategory_id || null, businessCategoryId);
                              }
                            }}
                            className="px-2 py-1 text-xs rounded border"
                            style={{ 
                              backgroundColor: 'var(--background-light)',
                              borderColor: 'var(--border-color)',
                              color: 'var(--text-primary)',
                              minWidth: '200px'
                            }}
                          >
                            <option value="">Select category...</option>
                            {businessCategories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessAccountingMapping;

